import { DatePicker as AntDatePicker, Grid, Input } from 'antd';
import type { DatePickerProps } from 'antd/es/date-picker';
import { Dayjs } from 'dayjs';
import React, { useCallback, useState } from 'react';
import {
  dayjsToInputValue,
  inputValueToDayjs,
  NativeType,
  parseStep,
  PickerType,
  resolveNativeType,
  wrapperCss,
} from './shared';

/**
 * 日期选择器（单日期），完全兼容 antd 的 DatePicker
 * 1. 当屏幕宽度大于 xs 时，使用 antd 的 DatePicker
 * 2. 当屏幕宽度小于等于 xs 时，使用原生 input 代替
 *  1. 根据 picker / showTime 参数，灵活决定 input 的 type
 */

type Props = Omit<
  DatePickerProps,
  'disabledDate' | 'format' | 'onFocus' | 'onBlur' | 'onKeyDown' | 'ref' | 'PickerClassNames' | 'classNames' | 'styles'
> & {
  format?: string;
};

export const DatePicker: React.FC<Props> = props => {
  const screens = Grid.useBreakpoint();
  const nativeType = resolveNativeType(props.picker as PickerType, !!props.showTime);

  // 宽屏、SSR、或原生不支持的类型 → 使用 antd DatePicker
  if (!screens?.xs || !nativeType) {
    return <AntDatePicker {...props} />;
  }
  // 窄屏 → 使用原生 input
  return <NativeDatePicker {...props} nativeType={nativeType} />;
};

/**
 * 窄屏下的原生 input 实现
 */
const NativeDatePicker: React.FC<Props & { nativeType: NativeType }> = ({
  value: valueProp,
  defaultValue,
  onChange,
  disabled,
  placeholder,
  className,
  style,
  nativeType,
  minDate,
  maxDate,
  format,
  prefix,
}) => {
  const [internalValue, setInternalValue] = useState<Dayjs | null>(defaultValue ?? null);

  const isControlled = valueProp !== undefined;
  const currentValue = isControlled ? valueProp : internalValue;

  const minStr = minDate ? dayjsToInputValue(minDate, nativeType, format) : undefined;
  const maxStr = maxDate ? dayjsToInputValue(maxDate, nativeType, format) : undefined;

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newDate = inputValueToDayjs(e.target.value);
      if (!isControlled) {
        setInternalValue(newDate);
      }
      onChange?.(newDate, e.target.value);
    },
    [onChange, isControlled]
  );

  return (
    <div className={`${wrapperCss} ${className ?? ''}`} style={style}>
      <Input
        prefix={prefix}
        type={nativeType}
        value={currentValue ? dayjsToInputValue(currentValue, nativeType, format) : ''}
        onChange={handleChange}
        placeholder={placeholder || '选择日期'}
        disabled={!!disabled}
        min={minStr}
        max={maxStr}
        step={parseStep(format)}
      />
    </div>
  );
};
